import type { FC } from 'react'
import styled from '@emotion/styled'

import Image from '@segment/matcha/components/Image'
import FeatureBoxBase, * as FeatureBox from '@segment/matcha/components/FeatureBox'
import ButtonText from '@segment/matcha/components/ButtonText'
import { ChevronRightIcon } from '@segment/matcha/components/Icons/Common'

import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'
import SectionBase, * as Section from '@segment/matcha/recipes/Section'

import getAbsoluteUrl from '@segment/utils/getAbsoluteUrl'

import { usePageConfig, type PageConfig } from '@segment/contexts/page'

import PredictiveAIPng2x from './assets/PredictiveAI@2x.png'
import PredictiveAIWebp from './assets/PredictiveAI.webp'
import PredictiveAIWebp2x from './assets/PredictiveAI@2x.webp'

const getCopy = (pageConfig: PageConfig) => ({
  overline: `Predictive AI`,
  heading: `Predict user behavior for better personalization`,
  list: [
    {
      heading: `CustomerAI Predictions`,
      description: `Without tapping technical teams, marketers can create more precise, predictive audiences out-of-the-box, trigger timely customer journeys, and personalize experiences based on a customer’s predictive lifetime value (LTV) or likelihood to purchase, churn, or perform any event tracked in Segment.`,
      cta: (
        <p>
          <ButtonText
            iconEnd={ChevronRightIcon}
            href={getAbsoluteUrl(`/solutions/ai/customerai-predictions/`, pageConfig)}
          >
            Learn more
          </ButtonText>
        </p>
      ),
    },
    {
      heading: `CustomerAI Recommendations`,
      description: `[Private Beta] Leverage AI to automatically determine which items are most likely to drive purchase so every customer experience is relevant.`,
    },
    {
      heading: `Build & Bring Your Own Models`,
      description: `Segment ensures you’re training your own ML models with real-time, identity-resolved customer data while helping to quickly and easily activate ML models from your data warehouse to all your downstream tools.`,
    },
    {
      heading: `Send Time & Channel Optimization`,
      description: `Coming soon: Avoid marketing fatigue and always reach customers on the best channel and at the most optimal time to drive engagement and conversion.`,
    },
  ],
})

const Predictive: FC = () => {
  const pageConfig = usePageConfig()
  const copy = getCopy(pageConfig)

  return (
    <SectionBase variant="2Col">
      <SectionCopy>
        <CopyTextBase>
          <CopyText.Overline>{copy.overline}</CopyText.Overline>
          <CopyText.Heading variant="h2" tag="h3">
            {copy.heading}
          </CopyText.Heading>

          {copy.list.map(({ heading, description, cta }) => (
            <FeatureBoxBase key={heading} headerPosition="top" alignment="left">
              <FeatureBox.Body>
                <FeatureBox.Title>{heading}</FeatureBox.Title>
                <FeatureBox.Text>{description}</FeatureBox.Text>
                {cta}
              </FeatureBox.Body>
            </FeatureBoxBase>
          ))}
        </CopyTextBase>
      </SectionCopy>

      <Section.Contents>
        <picture>
          <source type="image/webp" srcSet={`${PredictiveAIWebp}, ${PredictiveAIWebp2x} 2x`} />
          <StyledImage src={PredictiveAIPng2x} alt="Predictive AI" width="779" height="675" loading="lazy" />
        </picture>
      </Section.Contents>
    </SectionBase>
  )
}

const SectionCopy = styled(Section.Copy)`
  display: flex;
  align-items: center;

  ${({ theme }) => theme.breakpoints.up(`lg`)} {
    max-width: 440px;
    justify-self: end;
    align-self: center;
  }
`

const StyledImage = styled(Image)`
  filter: drop-shadow(${({ theme }) => theme.shadows[2]});
`

export default Predictive
